import { useCallback, useEffect, useRef, useState } from "react";
import { useContentStore } from "../../providers/stores/content-store";
import { PricingApi } from "../../api";
import { isNullOrEmpty, isNullOrEmptyString, isNullOrUndefined, parseNumber } from "../../helpers";
import { useAuth } from "../../hooks/useAuth";
import { usePricingStore } from "../../providers/stores/pricing-store";
import { RatesGrid } from './components/RatesGrid';

import logger from 'debug';
import useDidMount from "beautiful-react-hooks/useDidMount";
import { CloseDateDialogOptions, DateModal } from "./components/DateModal";
import { BulkEditModal, CloseDialogOptions } from "./components/BulkEditModal";

import './PricingPage.scss';
import { DateService, NotificationService } from "../../providers";
import { startOfDay } from "date-fns";

const debug = logger('pricing:pricingPage');

type DatesFilter = {
	startDate?: Date | null,
	endDate?: Date | null
}

export const PricingPage = () => {
	const sharedContent = useContentStore(state => state.sharedContent(state));
	const pricingContent = useContentStore(state => state.pricingContent(state));

	const { user } = useAuth();

	const { hotels,
		roomTypes,
		setRoomTypes,
		ratePlans,
		setRatePlans,
		rates,
		setRates,
		setHotels,
	} = usePricingStore();

	const [datesFilter, setDatesFilter] = useState({ startDate: null, endDate: null } as DatesFilter);
	const [isDateModalOpen, setDateModalOpen] = useState(false);
	const [isBulkEditModalOpen, setBulkEditModalOpen] = useState(false);

	const [selectedHotelId, setSelectedHotelId] = useState('');
	const [selectedRoomId, setSelectedRoomId] = useState('');
	const [selectedRatePlanId, setSelectedRatePlanId] = useState('');

	const didMount = useRef(false);
	const hotelsRef = useRef<HTMLSelectElement>(null);
	const roomsRef = useRef<HTMLSelectElement>(null);
	const ratePlansRef = useRef<HTMLSelectElement>(null);

	useDidMount(() => {
		initPricing();

		didMount.current = true;
	})

	const initPricing = async () => {
		let apiResponse = await PricingApi.initialize(user.token);
		if (isNullOrUndefined(apiResponse.error)) {
			const hotels = apiResponse.result;
			setHotels(hotels);

			const tmpNewHotelId = isNullOrEmpty(hotels) ? "" : hotels[0].hotelId;
			setSelectedHotelId(tmpNewHotelId);
			refreshRooms(tmpNewHotelId);
		}
	}

	const getParsedValues = useCallback(() => {
		let parsedHotelId = parseNumber(selectedHotelId);
		if (hotelsRef.current)
			parsedHotelId = parseNumber(hotelsRef.current.value);

		let parsedRoomId = parseNumber(selectedRoomId);
		if (roomsRef.current)
			parsedRoomId = parseNumber(roomsRef.current.value);

		let parsedRatePlanId = parseNumber(selectedRatePlanId);
		if (ratePlansRef.current)
			parsedRatePlanId = parseNumber(ratePlansRef.current.value);

		return { parsedHotelId, parsedRoomId, parsedRatePlanId };
	}, [selectedHotelId, selectedRoomId, selectedRatePlanId]);

	const onHotelSelected = (event: React.ChangeEvent<HTMLSelectElement>) => {
		debug('onHotelSelected.val', event.target.value);

		const hotelId = Number(event.target.value);

		if (isNaN(hotelId) || !canContinue())
			return;

		setSelectedHotelId(event.target.value);
		refreshRooms(event.target.value);
	}

	const refreshRooms = async (hotelId?: string) => {
		let { parsedHotelId } = getParsedValues();
		if (!isNullOrUndefined(hotelId))
			parsedHotelId = parseNumber(hotelId);

		if (isNullOrUndefined(parsedHotelId))
			return;

		debug('refreshRooms: something called me...');
		let apiResponse = await PricingApi.getRoomTypes(user.token, { hotelId: parsedHotelId });
		if (isNullOrUndefined(apiResponse.error)) {
			const roomTypes = apiResponse.result;
			setRoomTypes(roomTypes);

			const tmpNewRoomId = isNullOrEmpty(roomTypes) ? "" : roomTypes[0].roomTypeId;
			setSelectedRoomId(tmpNewRoomId);
			refreshRatePlans(tmpNewRoomId);
		}
	}

	const onRoomSelected = async (event: React.ChangeEvent<HTMLSelectElement>) => {
		debug('onRoomSelected.val', event.target.value);

		const roomTypeId = Number(event.target.value);
		if (isNaN(roomTypeId) || !canContinue())
			return;

		setSelectedRoomId(event.target.value);
		refreshRatePlans(event.target.value);
	}

	const refreshRatePlans = async (roomId?: string) => {
		let { parsedHotelId, parsedRoomId } = getParsedValues();
		if (!isNullOrUndefined(roomId))
			parsedRoomId = parseNumber(roomId);

		if (isNullOrUndefined(parsedHotelId) || isNullOrUndefined(parsedRoomId)) {
			debug('refreshRatePlans: missing datar...', parsedHotelId, parsedRoomId);
			return;
		}

		debug('refreshRatePlans: something called me...');

		let apiResponse = await PricingApi.getRatePlans(user.token, { hotelId: parsedHotelId, roomTypeId: parsedRoomId });
		if (isNullOrUndefined(apiResponse.error)) {
			const ratePlans = apiResponse.result;
			setRatePlans(ratePlans);

			const tmpNewRatePlanId = isNullOrEmpty(ratePlans) ? "" : ratePlans[0].ratePlanId;
			setSelectedRatePlanId(tmpNewRatePlanId);
			refreshRates(tmpNewRatePlanId.toString());
		}
	}

	const onRatePlanSelected = async (event: React.ChangeEvent<HTMLSelectElement>) => {
		debug('onRatePlanSelected.val', event.target.value);

		const ratePlanId = Number(event.target.value);
		if (isNaN(ratePlanId) || !canContinue())
			return;

		setSelectedRatePlanId(event.target.value);
		refreshRates(event.target.value);
	}

	const refreshRates = async (ratePlanId?: string, filterStartDate?: Date | null | undefined, filterEndDate?: Date | null | undefined) => {
		debug('refreshRates:call', ratePlanId, filterStartDate, filterEndDate);
		let { parsedHotelId, parsedRoomId, parsedRatePlanId } = getParsedValues();
		if (!isNullOrUndefined(ratePlanId) && !isNullOrEmptyString(ratePlanId))
			parsedRatePlanId = parseNumber(ratePlanId);

		if (isNullOrUndefined(parsedHotelId) || isNullOrUndefined(parsedRoomId) || isNullOrUndefined(parsedRatePlanId)) {
			debug('failed refreshRates attempt', parsedHotelId, parsedRoomId, parsedRatePlanId);
			return;
		}

		if (parsedHotelId === 0 || parsedRoomId === 0 || parsedRatePlanId === 0) {
			debug('failed refreshRates attempt', parsedHotelId, parsedRoomId, parsedRatePlanId);
			return;
		}

		debug('refreshRates attempt', parsedHotelId, parsedRoomId, parsedRatePlanId);
		const { startDate, endDate } = datesFilter;
		if (isNullOrUndefined(filterStartDate))
			filterStartDate = startDate;
		if (isNullOrUndefined(filterEndDate))
			filterEndDate = endDate;

		let apiResponse = await PricingApi.getRates(user.token, {
			hotelId: parsedHotelId,
			roomTypeId: parsedRoomId,
			ratePlanId: parsedRatePlanId,
			startDate: filterStartDate,
			endDate: filterEndDate
		});
		debug('refreshRates:response', apiResponse);
		if (isNullOrUndefined(apiResponse.error)) {
			setRates(apiResponse.result.rates);

		}
	}

	const onDateModalClosed = (dateOpts?: CloseDateDialogOptions) => {
		setDateModalOpen(false);

		if (!isNullOrUndefined(dateOpts)) {
			const { startDate, endDate } = dateOpts || {};
			debug('onDateModalClosed', startDate, endDate);
			setDatesFilter({ startDate, endDate });
			refreshRates('', startDate, endDate);
		}
	}

	const onEditRangeClicked = () => {
		debug('onEditRangeClicked');
		setBulkEditModalOpen(true);
	}

	const onBulkEditModalClosed = (rateOpts?: CloseDialogOptions) => {
		debug('I got called bro');
		setBulkEditModalOpen(false);

		if (!isNullOrUndefined(rateOpts)) {
			const { rate, startDate, endDate } = rateOpts || { rate: {} };
			debug('onBulkEditModalClosed', rate);

			rates.forEach(existingRate => {
				let { date } = existingRate;
				let parsedDate = startOfDay(DateService.parse(date));
				if (DateService.isInRange(parsedDate, startDate, endDate)) {
					debug('onBulkEditModalClosed; will edit', existingRate);

					existingRate.bookingCode = rate?.bookingCode!;
					existingRate.cost = parseNumber(rate?.cost) ?? existingRate.cost;
					existingRate.isFreeSell = rate?.isFreeSell ?? existingRate.isFreeSell;
					existingRate.isSoldOut = rate?.isSoldOut ?? existingRate.isSoldOut;
					existingRate.minNights = parseNumber(rate?.minNights) ?? existingRate.minNights;
					existingRate.quantity = parseNumber(rate?.quantity) ?? existingRate.quantity;

					existingRate.isModified = true;
				}
			});

			setRates(rates);
		}
	}

	const onSaveClicked = async () => {
		let { parsedHotelId, parsedRoomId, parsedRatePlanId } = getParsedValues();

		if (isNullOrUndefined(parsedHotelId) || isNullOrUndefined(parsedRoomId) || isNullOrUndefined(parsedRatePlanId)) {
			debug('failed onSaveClicked attempt', parsedHotelId, parsedRoomId, parsedRatePlanId);
			return;
		}

		const modifiedRates = rates.filter(rate => rate.isModified === true);
		const hasErrors = !isNullOrEmpty(modifiedRates.filter(rate => !rate.isValid));

		if (hasErrors) {
			NotificationService.error('Unable to save rates.  Hover over the "Error" indicator for details on each row.');
			return;
		}

		if (!isNullOrEmpty(modifiedRates)) {
			let req = {
				filter: {
					hotelId: parsedHotelId,
					roomTypeId: parsedRoomId,
					ratePlanId: parsedRatePlanId,
					startDate: datesFilter.startDate,
					endDate: datesFilter.endDate
				},
				rates: modifiedRates
			};

			let apiResponse = await PricingApi.updateRates(user.token, req);
			if (isNullOrUndefined(apiResponse.error)) {
				if (apiResponse.result.success) {
					NotificationService.success('Rates updated.');
					refreshRates(/*selectedRatePlanId*/);
				}
				else
					NotificationService.error(apiResponse.result.message);
			} else {
				NotificationService.error('An error has occurred while processing your request.');
			}
		}

	}

	const onCancelClicked = () => {
		if (canContinue()) {
			refreshRates(/*selectedRatePlanId*/);
		}
	}

	const canContinue = () => {
		const modifiedRates = rates.filter(rate => rate.isModified === true);

		if (isNullOrEmpty(modifiedRates))
			return true;

		return window.confirm('You have modifications that have not been saved. Ok to lose those changes?');
	}

	return (
		<div>
			<div className="row">
				<div className="col">
					<div className="navPanels d-flex justify-content-center">
						<div>
							<h2>{pricingContent.hotelHeader}</h2>
							<div className="listWrapper d-flex">
								<select ref={hotelsRef} name="hotels" className="flex-fill" size={5} onChange={onHotelSelected} value={selectedHotelId}>
									{hotels.map((hotel, hotelIndex) => <option key={`hot-${hotelIndex}`} value={hotel.hotelId}>{hotel.name}</option>)}
								</select>
							</div>
						</div>
						<div>
							<h2>{pricingContent.roomHeader}</h2>
							<div className="listWrapper d-flex">
								<select ref={roomsRef} name="rooms" className="flex-fill" size={5} onChange={onRoomSelected} value={selectedRoomId}>
									{roomTypes.map((roomType, roomIndex) => <option key={`room-${roomIndex}`} value={roomType.roomTypeId}>{roomType.name}</option>)}
								</select>
							</div>
						</div>
						<div>
							<h2>{pricingContent.rateHeader}</h2>
							<div className="listWrapper d-flex">
								<select ref={ratePlansRef} name="rates" className="flex-fill" size={5} onChange={onRatePlanSelected} value={selectedRatePlanId}>
									{ratePlans.map((ratePlan, rateIndex) => <option key={`rate-${rateIndex}`} value={ratePlan.ratePlanId}>{ratePlan.name}</option>)}
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row my-2">
				<div className="col">
					<button type="button" className="btn gradientOrange" onClick={() => setDateModalOpen(true)}>{pricingContent.filterDatesButton}</button>
					<button type="button" className="btn gradientOrange ms-2" onClick={onEditRangeClicked}>{pricingContent.editRangeButton}</button>
				</div>
			</div>

			<div className="row dataContainer">
				<div className="col-10">
					<div className="tableContainer" style={{ maxHeight: '800px', overflow: 'scroll' }}>
						<div>
							<RatesGrid
								pricingContent={pricingContent}
								rates={rates}
							/>
						</div>
					</div>
				</div>
				<div className="col-2">
					<div className="toolContainer">
						<div className="d-flex flex-column">
							<button type="button" className="btn gradientBlue" onClick={onSaveClicked}>{sharedContent.saveButton}</button>
							<button type="button" className="btn standard mt-2" onClick={onCancelClicked}>{sharedContent.cancelButton}</button>
						</div>
						<div className="legend gradient-gray">
							<h3>{pricingContent.legendHeader}</h3>
							<ul>
								<li className="d-flex"><span className="indicator">{`\u2B24`}</span><span className="label">{pricingContent.legendRateExisting}</span></li>
								<li className="d-flex"><span className="indicator modified">{`\u2B24`}</span><span className="label">{pricingContent.legendRateChanges}</span></li>
								<li className="d-flex"><span className="indicator error">{`\u2B24`}</span><span className="label">{pricingContent.legendRateError}</span></li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			<DateModal
				isOpen={isDateModalOpen}
				closeDialog={onDateModalClosed} />
			<BulkEditModal
				isOpen={isBulkEditModalOpen}
				closeDialog={onBulkEditModalClosed} />

		</div>
	);
};